/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   Store unico wishlist (design §2.2): stato in memoria derivato da
 *            UNA fonte per modalità — LocalDriver (guest) o ServerDriver (auth),
 *            mai duplicato. Boot: guest → read localStorage; auth → merge-at-boot
 *            (§3) + GET lista. Toggle guest sincrono; auth OTTIMISTICO con
 *            rollback su errore (design §4). Gli eventi egi:wishlist:changed/
 *            merged sono l'unico canale verso le superfici (badge, pagina, card).
 */

import {
    appendWithEviction,
    buildItem,
    clearStoredItems,
    readStoredItems,
    toggleInList,
    writeStoredItems,
} from './local-driver';
import { runMergeAtBoot } from './merge';
import { isAbortError, WishlistServerDriver } from './server-driver';
import {
    CHANGED_EVENT,
    MERGED_EVENT,
    type WishlistChangedAction,
    type WishlistChangedDetail,
    type WishlistConfig,
    type WishlistItem,
    type WishlistMergedDetail,
} from './types';

export type ToggleAction = 'added' | 'removed';

export class WishlistStore {
    /** Boot completato: stato allineato alla fonte (localStorage o server). */
    public readonly ready: Promise<void>;

    private items: WishlistItem[] = [];
    private readonly driver: WishlistServerDriver | null;

    public constructor(config: WishlistConfig) {
        this.driver = config.authenticated ? new WishlistServerDriver(config.endpoints) : null;
        // Boot differito a un microtask: chi crea lo store può ancora agganciare
        // i listener egi:wishlist:* nello stesso tick, PRIMA di ogni dispatch.
        this.ready = Promise.resolve().then(() => this.boot());
    }

    public has(id: string): boolean {
        return this.items.some((item) => item.id === id);
    }

    public count(): number {
        return this.items.length;
    }

    public list(): ReadonlyArray<WishlistItem> {
        return this.items;
    }

    /**
     * Toggle: guest = sincrono su localStorage (sempre "riuscito"); auth =
     * ottimistico (flip immediato) + rollback su errore server. AbortError
     * (toggle superato da uno più recente) è rilanciato SENZA rollback: lo
     * stato lo decide la richiesta più recente.
     */
    public async toggle(input: Omit<WishlistItem, 'addedAt'>): Promise<ToggleAction> {
        if (input.id === '') {
            throw new Error('Wishlist toggle: missing egi id');
        }

        if (this.driver === null) {
            const result = toggleInList(this.items, input);
            this.items = result.items;
            writeStoredItems(this.items);
            this.dispatchChanged(result.action === 'added' ? 'add' : 'remove', input.id);
            return result.action;
        }

        const wasIn = this.has(input.id);
        const action: ToggleAction = wasIn ? 'removed' : 'added';
        const previous = this.items;
        this.items = wasIn
            ? this.items.filter((item) => item.id !== input.id)
            : appendWithEviction(this.items, buildItem(input));
        this.dispatchChanged(wasIn ? 'remove' : 'add', input.id);

        try {
            const result = await this.driver.toggle(input.id);
            // Il server è la fonte di verità: divergenza (altra tab) → riallinea.
            if (result.inWishlist !== !wasIn) {
                await this.refreshFromServer();
            }
            return action;
        } catch (error) {
            if (isAbortError(error)) {
                throw error;
            }
            this.items = previous; // rollback visivo+ARIA (design §4)
            this.dispatchChanged('sync');
            throw error;
        }
    }

    /** Rimozione esplicita (pagina wishlist) — idempotente, ottimistica per l'auth. */
    public async remove(id: string): Promise<void> {
        if (!this.has(id)) {
            return;
        }

        if (this.driver === null) {
            this.items = this.items.filter((item) => item.id !== id);
            writeStoredItems(this.items);
            this.dispatchChanged('remove', id);
            return;
        }

        const previous = this.items;
        this.items = this.items.filter((item) => item.id !== id);
        this.dispatchChanged('remove', id);

        try {
            await this.driver.remove(id);
        } catch (error) {
            if (isAbortError(error)) {
                throw error;
            }
            this.items = previous;
            this.dispatchChanged('sync');
            throw error;
        }
    }

    /**
     * Riallineamento esplicito al server (M-EGI-385): il move-to-cart cambia la
     * wishlist FUORI dal toggle (la riga transita di stato) — il modulo cart lo
     * invoca dopo ogni move riuscito per riportare items/badge alla verità server.
     * No-op per il guest (il cart è server-only, ADR-C7).
     */
    public async resync(): Promise<void> {
        await this.refreshFromServer();
    }

    private async boot(): Promise<void> {
        if (this.driver === null) {
            this.items = readStoredItems();
            this.dispatchChanged('sync');
            return;
        }

        const driver = this.driver;
        const mergeOutcome = await runMergeAtBoot({
            read: readStoredItems,
            clear: clearStoredItems,
            merge: (items) => driver.merge(items),
        });

        await this.refreshFromServer();

        if (mergeOutcome !== null) {
            this.dispatchMerged(mergeOutcome);
        }
    }

    private async refreshFromServer(): Promise<void> {
        if (this.driver === null) {
            return;
        }
        try {
            const result = await this.driver.fetchList();
            this.items = result.items;
        } catch {
            // Non bloccante: errore già loggato dal driver; lo stato in memoria
            // resta quello corrente e si riallinea al prossimo page load.
        }
        this.dispatchChanged('sync');
    }

    private dispatchChanged(action: WishlistChangedAction, id?: string): void {
        const detail: WishlistChangedDetail = { count: this.count(), action };
        if (id !== undefined) {
            detail.id = id;
        }
        document.dispatchEvent(new CustomEvent<WishlistChangedDetail>(CHANGED_EVENT, { detail }));
    }

    private dispatchMerged(detail: WishlistMergedDetail): void {
        document.dispatchEvent(new CustomEvent<WishlistMergedDetail>(MERGED_EVENT, { detail }));
    }
}
